<script setup>
import { computed } from 'vue'
import useUser from '../../../../Composables/User/useUser.ts'
import useConfig from '../../../../Composables/useConfig'
import FieldCreator from '../UI/Form/FieldCreator.vue'

const { isReadOnly } = useUser()
const { isTheme } = useConfig()

const props = defineProps({
  object: { type: Object, required: true },
  formData: { type: Object, required: true },
  formRef: { type: String, default: 'formcontract' },
  formInstance: { type: Object, default: null },
  labelTop: { type: Boolean, default: false },
  twoCols: { type: Boolean, default: false },
  refreshFields: { type: Array, default: () => [] },
  classes: { type: String, default: '' },
  clientIdList: { type: Object, default: null },
})

const emit = defineEmits([
  'add-buyer',
  'update-buyer',
  'add-salesman',
  'update:surrenderSigner',
  'add-surrenderSigner',
  'update:surrender',
  'add-surrender',
  'update:surrenderApproverSigners',
  'add-surrenderApproverSigners',
])

const canAddOrEditUser = computed(() => !isReadOnly.value)

const canAddSalesman = computed(
  () =>
    !isReadOnly.value &&
    props.object?.supplier &&
    props.object.supplier.id &&
    props.object.supplier.id !== props.object.client?.id,
)

const signerBlockClass = computed(() => ({
  'signers-grid': props.twoCols,
  [props.classes]: props.classes !== '',
}))

const supplierId = computed(() => {
  return props.clientIdList?.supplier ?? props.object?.supplierId ?? props.object?.supplier?.id
})

const addUser = (type, data) => {
  emit(`add-${type}`, data)
}

const updateUser = (type, userObj) => {
  emit(`update-${type}`, userObj)
}

const shouldRefresh = (fieldName) => {
  return props.refreshFields.includes(fieldName)
}

const signerConfs = computed(() => {
  const signersOrder = Object.keys(props.formInstance?.def?.signers) || []
  return [
    {
      name: 'buyer',
      props: {
        addUser: !isReadOnly.value,
        addUserPlaceholder: isReadOnly.value,
        contractId: props.object?.contractId ?? props.object?.id,
        clientId: props.object?.clientId ?? props.object?.client?.id,
      },
    },
    {
      name: 'companySigner',
      props: {
        addUserPlaceholder: true,
      },
    },
    {
      name: 'supplierSigner',
      props: {
        addUserPlaceholder: true,
      },
    },
    ...(supplierId.value && !isTheme('yneia')
      ? [
          {
            name: 'salesman',
            props: {
              addUser: canAddSalesman.value,
              addUserPlaceholder: !canAddSalesman.value,
              contractId: props.object?.contractId ?? props.object?.id,
              clientId: supplierId.value,
            },
          },
        ]
      : []),
    {
      name: 'clientApproverSigners',
      props: {
        multiple: true,
        multipleChoice: true,
        selectMultiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'surrenderSigner',
      props: {
        multiple: true,
        addUser: canAddOrEditUser.value,
        addUserPlaceholder: !canAddOrEditUser.value,
        contractId: props.object?.contractId ?? props.object?.id,
        entityId: props.object?.entity?.id,
      },
      events: {
        'update-user': (event) => updateUser('surrenderSigner', event),
        'get-select-change': (data) => addUser('surrenderSigner', data),
      },
    },
    {
      name: 'surrenderApproverSigners',
      props: {
        multiple: true,
        multipleChoice: true,
        selectMultiple: true,
        addUser: canAddOrEditUser.value,
        addUserPlaceholder: !canAddOrEditUser.value,
        contractId: props.object?.contractId ?? props.object?.id,
        entityId: props.object?.entity?.id,
      },
      events: {
        'update-user': (event) => updateUser('surrenderApproverSigners', event),
        'get-select-change': (data) => addUser('surrenderApproverSigners', data),
      },
    },
    {
      name: 'surrender',
      props: {
        multiple: true,
        multipleChoice: true,
        selectMultiple: true,
        addUser: canAddOrEditUser.value,
        addUserPlaceholder: !canAddOrEditUser.value,
        contractId: props.object?.contractId ?? props.object?.id,
        clientId: props.object?.clientId ?? props.object?.client?.id,
      },
      events: {
        'update-user': (event) => updateUser('surrender', event),
        'get-select-change': (data) => addUser('surrender', data),
      },
    },
    {
      name: 'deliverySigner',
      props: {
        multiple: true,
        addUser: !isReadOnly.value,
        addUserPlaceholder: isReadOnly.value,
        contractId: props.object?.contractId,
        clientId: props.object?.clientId,
      },
    },
    {
      name: 'supSensitiveSigner',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'refinancerSigner',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'internalApprovers',
      props: {
        multiple: true,
        multipleChoice: true,
        selectMultiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'buyerDirector',
      props: {
        multiple: true,
        addUser: !isReadOnly.value,
        addUserPlaceholder: isReadOnly.value,
        contractId: props.object?.id,
        clientId: props.object?.client?.id,
      },
    },
    {
      name: 'companySigner2',
      props: {
        addUserPlaceholder: true,
      },
    },
    {
      name: 'refinancerSigner2',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'coclients',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'oldclients',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'subclients',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'manager',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'guarantor',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'brokerSigner',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    {
      name: 'platform',
      props: {
        multiple: true,
        addUserPlaceholder: true,
      },
    },
    // sort signers by fieldOrderPriority from form definition
  ].sort((a, b) => {
    const indexA = signersOrder.indexOf(a.name)
    const indexB = signersOrder.indexOf(b.name)
    return indexA - indexB
  })
})
</script>

<template>
  <div :class="signerBlockClass">
    <FieldCreator
      v-for="signerConf in signerConfs"
      :key="signerConf.name"
      :form-data="formData"
      :label-left-equal="!labelTop"
      :name="`signers.${signerConf.name}`"
      :form-ref="formRef"
      domain="contract"
      :label-top="labelTop"
      :refresh="shouldRefresh(`signers.${signerConf.name}`)"
      :add-user="signerConf.props.addUser"
      :add-user-placeholder="signerConf.props.addUserPlaceholder"
      :multiple="signerConf.props.multiple"
      :multiple-choice="signerConf.props.multipleChoice"
      :select-multiple="signerConf.props.selectMultiple"
      :contract-id="signerConf.props.contractId"
      :client-id="signerConf.props.clientId"
      :entity-id="signerConf.props.entityId"
      user-select-allow-grow
      v-on="signerConf.events ?? {}"
    />
  </div>
</template>

<style scoped>
.signers-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-flow: row;
  column-gap: 2.5em;
}
</style>
